import { jsx as _jsx } from "react/jsx-runtime";
/**
 * {@link AuthProvider} — wires an {@link AuthClient} to React state and the
 * {@link TokenRefreshScheduler}, exposing session/auth actions via context.
 *
 * Responsibilities:
 *   - On mount, restore any existing session (`getCurrentSession`).
 *   - `signIn` / `signOut` (Req 23.3).
 *   - Schedule proactive token refresh before expiry, and refresh on-demand
 *     when a token is requested while expired/expiring (Req 23.6).
 *
 * The Cognito SDK is reached only through the injected `authClient`, so apps in
 * tests can pass a fake and exercise the full provider without network calls.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AuthContext } from './context.js';
import { TokenRefreshScheduler, isExpiredOrExpiring, } from './token-refresh.js';
export function AuthProvider({ authClient, children, refreshSkewMs, timers, }) {
    const [session, setSession] = useState(null);
    const [status, setStatus] = useState('loading');
    // Keep a ref to the latest session so async callbacks (getAccessToken) read
    // current state without being re-created on every session change.
    const sessionRef = useRef(null);
    sessionRef.current = session;
    const applySession = useCallback((next) => {
        sessionRef.current = next;
        setSession(next);
        setStatus('authenticated');
    }, []);
    const scheduler = useMemo(() => new TokenRefreshScheduler({
        authClient,
        skewMs: refreshSkewMs,
        timers,
        onRefreshed: applySession,
        onError: () => {
            // Refresh token invalid/expired → treat as signed out.
            sessionRef.current = null;
            setSession(null);
            setStatus('unauthenticated');
        },
    }), [authClient, refreshSkewMs, timers, applySession]);
    // Restore an existing session on mount; start refresh scheduling if present.
    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const existing = await authClient.getCurrentSession();
                if (cancelled)
                    return;
                if (existing) {
                    applySession(existing);
                    scheduler.start(existing);
                }
                else {
                    setStatus('unauthenticated');
                }
            }
            catch {
                if (!cancelled)
                    setStatus('unauthenticated');
            }
        })();
        return () => {
            cancelled = true;
            scheduler.stop();
        };
    }, [authClient, scheduler, applySession]);
    const signIn = useCallback(async (input) => {
        const next = await authClient.signIn(input);
        applySession(next);
        scheduler.start(next);
    }, [authClient, scheduler, applySession]);
    const signOut = useCallback(async () => {
        scheduler.stop();
        await authClient.signOut();
        sessionRef.current = null;
        setSession(null);
        setStatus('unauthenticated');
    }, [authClient, scheduler]);
    const signUp = useCallback((input) => authClient.signUp(input), [authClient]);
    const confirmSignUp = useCallback((email, code) => authClient.confirmSignUp(email, code), [authClient]);
    const getAccessToken = useCallback(async () => {
        const current = sessionRef.current;
        if (!current)
            return null;
        // Refresh first if the token is expired or within the skew window (Req 23.6).
        if (isExpiredOrExpiring(current, Date.now(), refreshSkewMs)) {
            try {
                const refreshed = await scheduler.refreshNow();
                return refreshed.accessToken;
            }
            catch {
                return null;
            }
        }
        return current.accessToken;
    }, [scheduler, refreshSkewMs]);
    const getIdToken = useCallback(async () => {
        const current = sessionRef.current;
        if (!current)
            return null;
        if (isExpiredOrExpiring(current, Date.now(), refreshSkewMs)) {
            try {
                const refreshed = await scheduler.refreshNow();
                return refreshed.idToken;
            }
            catch {
                return null;
            }
        }
        return current.idToken;
    }, [scheduler, refreshSkewMs]);
    const value = useMemo(() => ({
        status,
        session,
        user: session?.user ?? null,
        signIn,
        signOut,
        signUp,
        confirmSignUp,
        getAccessToken,
        getIdToken,
    }), [status, session, signIn, signOut, signUp, confirmSignUp, getAccessToken, getIdToken]);
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
//# sourceMappingURL=AuthProvider.js.map